import { useEffect, useMemo, useState } from "react";
import { X, Tags, Image as ImageIcon } from "lucide-react";

function slugify(input = "") {
  return input
    .toString()
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function CreateCategoryModal({
  open,
  onClose,
  onCreate,
  categories = [],
}) {
  const parentOptions = useMemo(
    () => categories.filter((c) => c.parentId == null),
    [categories],
  );

  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    parentId: "",
    imageFile: null, // ⬅️ ONLY stored, not uploaded
  });

  const [autoSlug, setAutoSlug] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({
      name: "",
      slug: "",
      description: "",
      parentId: "",
      imageFile: null,
    });
    setAutoSlug(true);
    setError("");
  }, [open]);

  function setField(name, value) {
    setForm((p) => ({ ...p, [name]: value }));
  }

  function handleNameChange(v) {
    setField("name", v);
    if (autoSlug) setField("slug", slugify(v));
  }

  function submit(e) {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) return setError("Category name is required.");
    if (!form.slug.trim()) return setError("Slug is required.");

    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      description: form.description.trim() || null,
      parentId: form.parentId ? Number(form.parentId) : null,
      imageFile: form.imageFile,
    };

    onCreate?.(payload);
  }

  if (!open) return null;

  const previewUrl = form.imageFile
    ? URL.createObjectURL(form.imageFile)
    : null;

  return (
    <div className="inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-black/40 rounded-2xl"
        onClick={onClose}
      />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-slate-200">
          {/* header */}
          <div className="flex items-center justify-between border-b p-4">
            <div className="flex items-center gap-2">
              <Tags className="h-5 w-5 text-slate-700" />
              <div className="font-semibold text-slate-900">
                Create Category
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-xl p-2 hover:bg-slate-100"
            >
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </div>

          <form onSubmit={submit} className="p-4 space-y-4">
            {error && (
              <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            )}

            {/* 🔥 IMAGE SECTION (FIRST) */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <ImageIcon className="h-4 w-4" />
                Category Image
              </div>

              <div className="mt-3 flex items-center gap-4">
                <div className="h-20 w-20 rounded-xl bg-slate-100 ring-1 ring-slate-200 overflow-hidden flex items-center justify-center">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-xs text-slate-400">No Image</span>
                  )}
                </div>

                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setField("imageFile", e.target.files?.[0] || null)
                    }
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Image will be uploaded later when you handle API logic.
                  </p>
                </div>
              </div>
            </div>

            {/* NAME */}
            <div className="grid gap-2">
              <label className="text-sm text-slate-600">Name</label>
              <input
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-200"
                placeholder="Home Decor"
              />
            </div>

            {/* SLUG */}
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <label className="text-sm text-slate-600">Slug</label>
                <label className="flex items-center gap-2 text-xs text-slate-500">
                  <input
                    type="checkbox"
                    checked={autoSlug}
                    onChange={(e) => setAutoSlug(e.target.checked)}
                  />
                  Auto-generate
                </label>
              </div>
              <input
                value={form.slug}
                onChange={(e) => {
                  setAutoSlug(false);
                  setField("slug", e.target.value);
                }}
                className="rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-200"
                placeholder="home-decor"
              />
            </div>

            {/* PARENT */}
            <div className="grid gap-2">
              <label className="text-sm text-slate-600">Parent Category</label>
              <select
                value={form.parentId}
                onChange={(e) => setField("parentId", e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-200"
              >
                <option value="">No parent (Top-level)</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* DESCRIPTION */}
            <div className="grid gap-2">
              <label className="text-sm text-slate-600">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                className="min-h-[90px] rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-200"
                placeholder="Optional description"
              />
            </div>

            {/* ACTIONS */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
              >
                Create Category
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
